import React from 'react';
import { CheckCircle, Users, Target, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-6 font-poppins">
              About Mayo Design
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              A thoughtful interior design studio based in Sector 86, Gurugram, dedicated to transforming homes into beautifully crafted spaces.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/5922204/pexels-photo-5922204.jpeg"
                alt="Mayo Design Team"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-6 font-poppins">
                Who We Are
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Mayo Design is a professional interior design studio rooted in Sector 86, Gurugram, with a passion for creating thoughtful, well-planned interiors that enhance daily living.
                </p>
                <p>
                  Our expertise lies in complete home reconstructions – taking existing houses and transforming them into beautifully reimagined spaces through structural changes, custom planning, and bespoke furniture design.
                </p>
                <p>
                  What sets us apart is our holistic approach: we don't just design interiors, we understand the architecture, coordinate with contractors, manage timelines, and ensure every detail aligns with your vision and lifestyle.
                </p>
                <p>
                  We believe in responsive communication, professional execution, and delivering exceptional value. Every project is a collaboration where your needs guide our creative process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#E8DCC4]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4 font-poppins">
              What We Stand For
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Our core values guide every project and relationship we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: CheckCircle,
                title: 'Quality First',
                description: 'Uncompromising commitment to craftsmanship and attention to detail in every project.'
              },
              {
                icon: Users,
                title: 'Client-Centric',
                description: 'Your vision, needs, and lifestyle are at the heart of every design decision we make.'
              },
              {
                icon: Target,
                title: 'Thoughtful Design',
                description: 'Every space is carefully planned to be both beautiful and highly functional.'
              },
              {
                icon: Award,
                title: 'Professional Excellence',
                description: 'Transparent communication, timely delivery, and exceptional service throughout.'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#2F4538] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#2C2C2C] mb-3 font-poppins">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-6 font-poppins text-center">
              Service Geography
            </h2>
            <div className="bg-[#F5F1E8] rounded-lg p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                We are proudly based in <span className="font-semibold text-[#2F4538]">Sector 86, Gurugram</span>, and primarily serve homeowners throughout Gurugram and the surrounding NCR regions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our local presence means we understand the unique architectural styles, space constraints, and design preferences of the area. We're always just a call away for site visits, material selections, and project coordination.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 bg-[#2F4538] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 font-poppins">
              Our Journey
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              Mayo Design was born from a simple belief: that every home deserves to be more than just a structure – it should be a reflection of the people who live in it.
            </p>
            <p className="text-lg leading-relaxed mb-8">
              With years of experience in turning existing homes into completely reimagined spaces, we've honed our expertise in reconstruction, custom planning, and bespoke furniture. Each project teaches us something new, and we bring that learning to every client we serve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <p className="text-xl font-semibold">Ready to start your project?</p>
              <a href="tel:+919910837533" className="text-2xl font-bold hover:underline">
                +91 99108 37533
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;